import { NewButton, NewButtonVariant } from "@visiblethread/react-components";
import { modalTypes } from "constants/types";
import useShowArchiveEntityModal from "hooks/useShowArchiveEntityModal";
import { Archive, ArchiveRestore, SquarePen, Trash2 } from "lucide-react";
import { bool } from "prop-types";
import { Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hideModal, showModal } from "slices/app/actions/modals";
import {
  archiveMiscAnalysis,
  deleteMiscAnalysisById,
  unarchiveMiscAnalysis,
} from "slices/miscAnalysis/actions";
import {
  setEditingMiscAnalysis,
  setShowEditPanel,
} from "slices/miscAnalysis/miscAnalysisSlice";
import { selectCurrentMiscAnalysis } from "slices/miscAnalysis/selectors";

import classes from "./Options.module.scss";

const Options = ({ isArchived = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showArchiveEntityModal = useShowArchiveEntityModal();
  const miscAnalysis = useSelector(selectCurrentMiscAnalysis);

  const handleEditClick = () => {
    dispatch(setEditingMiscAnalysis(miscAnalysis));
    dispatch(setShowEditPanel(true));
  };

  const handleArchiveClick = () => {
    showArchiveEntityModal({
      primarySlot: { entityType: "misc-analysis", label: "Misc Analysis" },
      entityName: miscAnalysis.name,
      onConfirm: async ({ statusIdsBySlot, note }) => {
        await dispatch(
          archiveMiscAnalysis(miscAnalysis.id, miscAnalysis.name, {
            archiveStatusId: statusIdsBySlot.primary,
            archiveNote: note,
          })
        );
        navigate("/misc-analysis/dashboard");
      },
    });
  };

  const handleUnarchiveClick = () => {
    dispatch(unarchiveMiscAnalysis(miscAnalysis.id));
    navigate("/misc-analysis/archived");
  };

  const handleDeleteClick = () => {
    dispatch(
      showModal(modalTypes.CONFIRMATION, {
        headerText: "Delete Misc Analysis",
        bodyText: (
          <span>
            Are you sure you want to delete <strong>{miscAnalysis.name}</strong>
            ? This action cannot be undone.
          </span>
        ),
        okButton: {
          text: "Delete",
          onOk: async () => {
            dispatch(hideModal(modalTypes.CONFIRMATION));
            await dispatch(
              deleteMiscAnalysisById(miscAnalysis.id, miscAnalysis.name)
            );
            navigate(
              isArchived
                ? "/misc-analysis/archived"
                : "/misc-analysis/dashboard"
            );
          },
        },
        cancelButton: {
          text: "Cancel",
          onCancel: () => dispatch(hideModal(modalTypes.CONFIRMATION)),
        },
      })
    );
  };

  return (
    <div className={classes.options}>
      {isArchived ? (
        <Fragment>
          <NewButton
            onClick={handleUnarchiveClick}
            variant={NewButtonVariant.l1_5}
            rightIcon={<ArchiveRestore size={14} />}
          >
            Unarchive
          </NewButton>
          <NewButton
            onClick={handleDeleteClick}
            variant={NewButtonVariant.red}
            leftIcon={<Trash2 size={14} />}
            aria-label="Delete"
          />
        </Fragment>
      ) : (
        <Fragment>
          <NewButton
            variant={NewButtonVariant.l1}
            onClick={handleEditClick}
            leftIcon={<SquarePen size={14} />}
          >
            Edit
          </NewButton>
          <NewButton
            variant={NewButtonVariant.l2}
            onClick={handleArchiveClick}
            rightIcon={<Archive size={14} />}
          >
            Archive
          </NewButton>
          <NewButton
            onClick={handleDeleteClick}
            variant={NewButtonVariant.red}
            leftIcon={<Trash2 size={14} />}
            aria-label="Delete"
          />
        </Fragment>
      )}
    </div>
  );
};

Options.propTypes = {
  isArchived: bool,
};

export default Options;
